import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow, Polyline, MarkerClusterer } from '@react-google-maps/api';
import { Select, DatePicker } from 'antd';
import moment from 'moment';
import 'moment-timezone';
import { useHistory } from "react-router-dom";
import { secureApi } from '../../Config/api';
import { GET } from '../../utils/apis';
import { translations } from '../../utils/constants';
import { convertItalianToLocalTime, fetchEmployeeName, verifyToken } from '../../utils/helpers';
import paths from '../../Config/paths';
import { useDispatch, useSelector } from 'react-redux';

const { Option } = Select;

const containerStyle = {
    width: '100%',
    height: 'calc(100vh - 71px)',
};

const libraries = ['geometry'];
const GOOGLE_MAPS_API_KEY = 'AIzaSyAOvlaMKD4C0dPSjCtNOi-DnwkW8s4pSbI';

const Maps = ({ language }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.authReducer?.user);
    const employeeNameList = useSelector((state) => state.employeeLogReducer?.nameList);
    const mapRef = useRef(null);
    const defaultCenter = { lat: 45.4642, lng: 9.19 };

    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedDate, setSelectedDate] = useState(moment());
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mapCenter, setMapCenter] = useState({ lat: 45.4642, lng: 9.19 });
    const [isMapsLoaded, setIsMapsLoaded] = useState(false);

    const clusterStyles = useMemo(() => [
        {
            textColor: 'red',
            url: `data:image/svg+xml;base64,${btoa(`
                <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">
                    <circle cx="30" cy="30" r="20" fill="#800080"/>
                    <text x="30" y="35" font-size="16" fill="white" text-anchor="middle" font-family="Arial" font-weight="bold">U</text>
                </svg>`)}`,
            height: 60,
            width: 60,
            textSize: 27,
            anchorText: [-18, 32],
        }
    ], []);

    useEffect(() => {
        if (!user || !employeeNameList) return;
        const allowedRoles = ['admin', 'superAdmin'];
        if (user && !allowedRoles.includes(user.role)) {
            history.push(paths?.HOME);
        }
    }, [user, employeeNameList, history]);

    useEffect(() => {
        if (user) {
            verifyToken(history, dispatch);
            fetchEmployeeName({ dispatch, user });
        }
    }, [user, dispatch, history]);

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
        setIsMapsLoaded(true);
    }, []);

    const fetchMapData = async (dateValue) => {
        try {
            setLoading(true);
            const dateParam = dateValue ? moment(dateValue).format('YYYY-MM-DD') : '';
            const employeeId = selectedEmployee ? JSON.parse(selectedEmployee).id : null;
            const userId = user?.id;

            const response = await secureApi.get(
                `${GET.GET_MAP_DATA}?date=${dateParam}&userId=${userId}&selectedUsers=${employeeId}`
            );

            if (response?.success && Array.isArray(response?.data)) {
                const formatted = response.data
                    ?.filter((e) => e.ina_lat && e.ina_long)
                    ?.map((item) => {
                        const entryLocal = convertItalianToLocalTime(item.entry_time);
                        const exitLocal = convertItalianToLocalTime(item.exit_time);

                        return {
                            id: item.day_id,
                            name: item.user_name,
                            email: item.email,
                            entryTime: entryLocal ? entryLocal.format('hh:mm A') : '--',
                            exitTime: exitLocal ? exitLocal.format('hh:mm A') : '--',
                            ina_lat: parseFloat(item.ina_lat),
                            ina_long: parseFloat(item.ina_long),
                            exi_lat: item.exi_lat ? parseFloat(item.exi_lat) : null,
                            exi_long: item.exi_long ? parseFloat(item.exi_long) : null,
                        };
                    });

                setEmployees(formatted);

                if (formatted.length > 0) {
                    const firstLoc = { lat: formatted[0].ina_lat, lng: formatted[0].ina_long };
                    setMapCenter(firstLoc);
                    if (mapRef.current) {
                        mapRef.current.panTo(firstLoc);
                        mapRef.current.setZoom(15);
                    }
                }
            } else {
                setEmployees([]);
                setMapCenter(defaultCenter);
            }
        } catch (err) {
            console.error('Error fetching map data:', err.message);
            setEmployees([]);
            setMapCenter(defaultCenter);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMapData(selectedDate);
    }, [selectedDate, selectedEmployee]);

    const handleEmployeeSelect = (value) => {
        const emp = JSON.parse(value);
        setSelectedEmployee(value);
        if (mapRef.current && emp.ina_lat && emp.ina_long) {
            const center = { lat: emp.ina_lat, lng: emp.ina_long };
            mapRef.current.panTo(center);
            mapRef.current.setZoom(15);
            setMapCenter(center);
            setTimeout(() => {
                setSelectedLocation(emp);
                setSelectedType('start');
            }, 300);
        }
    };

    const handleDateChange = (date) => {
        setSelectedDate(date || moment());
        setSelectedEmployee(null);
        setSelectedLocation(null);
    };

    const getUserInitial = (name) => name ? name[0].toUpperCase() : 'U';

    const generateRandomColor = (name) => {
        const colors = ['#4285F4', '#DB4437', '#F4B400', '#0F9D58', '#AB47BC', '#00ACC1'];
        const index = name ? name.length % colors.length : 0;
        return colors[index];
    };

    return (
        <>
            <div className='map-date-picker-div'>
                <div className='map-date-picker-inner-div'>
                    <Select
                        className='map-vehicle-select'
                        showSearch
                        placeholder={translations[language]?.selectEmployees}
                        onChange={handleEmployeeSelect}
                        value={selectedEmployee}
                        style={{ width: 200, marginRight: '16px' }}
                        loading={loading}
                    >
                        {employeeNameList?.map((e) => (
                            <Option key={e?.id} value={JSON.stringify(e)}>{e?.user_name}</Option>
                        ))}
                    </Select>

                    <DatePicker
                        format='YYYY-MM-DD'
                        value={selectedDate}
                        onChange={handleDateChange}
                        disabledDate={(current) => current && current > moment().endOf('day')}
                        className='map-date-picker'
                    />
                </div>
            </div>

            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={mapCenter}
                    zoom={12}
                    onLoad={onMapLoad}
                    options={{ gestureHandling: 'greedy', disableDefaultUI: true }}
                >
                    {loading && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                zIndex: 9999,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '1.5em',
                                color: '#333',
                                backgroundColor: 'rgb(0 0 0 / 30%)',
                            }}
                        >
                            <div className='spinner-border' role='status'>
                                <div className='dot-spinner'>
                                    {[...Array(8)].map((_, i) => (
                                        <div key={i} className='dot-spinner__dot'></div>
                                    ))}
                                </div>
                                <span className='sr-only'>{translations[language]?.loadingMap}...</span>
                            </div>
                        </div>
                    )}

                    {isMapsLoaded && window.google && (
                        <MarkerClusterer options={{ styles: clusterStyles, minimumClusterSize: 2 }}>
                            {(clusterer) => (
                                <>
                                    {employees.map((e) => (
                                        <React.Fragment key={e.id}>
                                            <Marker
                                                position={{ lat: e.ina_lat, lng: e.ina_long }}
                                                clusterer={clusterer}
                                                icon={{
                                                    url: `data:image/svg+xml;base64,${btoa(`
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                                                            <circle cx="20" cy="20" r="18" fill="${generateRandomColor(e.name)}" />
                                                            <text x="50%" y="50%" font-size="20" fill="white" text-anchor="middle" dominant-baseline="central" font-family="Arial">
                                                                ${getUserInitial(e.name)}
                                                            </text>
                                                        </svg>`)}`,
                                                    anchor: new window.google.maps.Point(20, 20)
                                                }}
                                                onClick={() => {
                                                    setSelectedLocation(e);
                                                    setSelectedType('start');
                                                    mapRef.current?.panTo({ lat: e.ina_lat, lng: e.ina_long });
                                                }}
                                            />
                                            {e.exi_lat && e.exi_long && (
                                                <>
                                                    <Polyline
                                                        path={[{ lat: e.ina_lat, lng: e.ina_long }, { lat: e.exi_lat, lng: e.exi_long }]}
                                                        options={{
                                                            strokeColor: '#FF0000',
                                                            strokeOpacity: 0.8,
                                                            strokeWeight: 2,
                                                            icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 }, offset: '0', repeat: '15px' }],
                                                        }}
                                                    />
                                                    <Marker
                                                        position={{ lat: e.exi_lat, lng: e.exi_long }}
                                                        icon={{
                                                            url: `data:image/svg+xml;base64,${btoa(`
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                                                    <circle cx="8" cy="8" r="6" fill="#0000FF" stroke="white" stroke-width="2"/>
                                                                </svg>`)}`,
                                                            anchor: new window.google.maps.Point(8, 8)
                                                        }}
                                                        onClick={() => {
                                                            setSelectedLocation(e);
                                                            setSelectedType('end');
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </>
                            )}
                        </MarkerClusterer>
                    )}

                    {selectedLocation && (
                        <InfoWindow
                            position={{
                                lat: selectedType === 'end' ? selectedLocation.exi_lat : selectedLocation.ina_lat,
                                lng: selectedType === 'end' ? selectedLocation.exi_long : selectedLocation.ina_long,
                            }}
                            onCloseClick={() => setSelectedLocation(null)}
                        >
                            <div style={{ lineHeight: '1.6', minWidth: '180px' }}>
                                <div><b>{translations[language]?.name}:</b> {selectedLocation.name}</div>
                                <div><b>{translations[language]?.email}:</b> {selectedLocation.email}</div>
                                <div><b>{translations[language]?.EntryTime}:</b> {selectedLocation.entryTime}</div>
                                <div><b>{translations[language]?.ExitTime}:</b> {selectedLocation.exitTime}</div>
                                <div><b>{translations[language]?.date}:</b> {selectedDate?.format('YYYY-MM-DD')}</div>
                            </div>
                        </InfoWindow>
                    )}
                </GoogleMap>
            </LoadScript>
        </>
    );
};

export default Maps